import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { faCopy as faCopySolid } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@kcftech/design-system';
import { FallbackRender, logger } from '@sentry/react';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { BasePage } from 'components/page/BasePage';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { getIsDebugModeSafely } from 'shared/debug/debug_store';
import styled from 'styled-components';
import { LinkButton } from 'styles/theme_styles/button';
import { tokens } from 'styles/tokens';
import { copyToClipboard } from 'utils/clipboard';

type FallbackRenderArgs = Parameters<FallbackRender>[0];

const Code = styled.pre`
    background-color: ${tokens.background.default};
    border-radius: 2px;
    width: min(80ch, 100%);
    overflow-x: auto;
    overflow-y: hidden;
`;

function ErrorDetails({ error }: { error: unknown }) {
    if (
        error === undefined ||
        error === null ||
        (typeof error === 'object' &&
            !(error instanceof Error) &&
            Object.keys(error).length === 0)
    ) {
        return <></>;
    }
    const data: [string, unknown][] =
        error instanceof Error
            ? [
                  ['message', error.message],
                  ['name', error.name],
                  ['cause', error.cause],
                  ['stack', error.stack],
              ]
            : [['details', error]];

    return (
        <details className="mt-10">
            <summary>View Error Details</summary>
            {data.map(([name, value]) => {
                const renderedValue =
                    typeof value === 'string'
                        ? value
                        : JSON.stringify(value, undefined, 2);
                return (
                    <>
                        <h3>
                            {name}{' '}
                            <HoverIconButton
                                color={tokens.text.primary}
                                icon={faCopy}
                                hoverIcon={faCopySolid}
                                onClick={() => copyToClipboard(renderedValue)}
                            />
                        </h3>
                        <Code>{renderedValue}</Code>
                    </>
                );
            })}
        </details>
    );
}

export function GlobalErrorPage(props: Partial<FallbackRenderArgs>) {
    useEffect(() => {
        logger.warn('GlobalErrorPage', {
            path: window.location.pathname,
        });
    }, []);

    return (
        <BasePage>
            <h1>An error occurred</h1>
            <p>
                An error occurred while trying to access this page. If this error
                persists, please contact your administrator.
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
                <LinkButton href="/">Go Home</LinkButton>
            </div>
            {getIsDebugModeSafely() && <ErrorDetails error={props.error} />}
        </BasePage>
    );
}

export function ErrorPage(props: Partial<FallbackRenderArgs>) {
    const location = useLocation();

    const originalLocationRef = useRef(location.key);
    useEffect(() => {
        if (originalLocationRef.current !== location.key) {
            props.resetError?.();
        }
    }, [location.key, props]);

    const { eventId } = props;

    return (
        <BasePage>
            <h1>An error occurred</h1>
            <p>
                An error occurred while trying to access this page. If this error
                persists, please contact your administrator.
            </p>
            {eventId && (
                <div>
                    <strong>
                        Event ID{' '}
                        <HoverIconButton
                            color={tokens.text.primary}
                            icon={faCopy}
                            hoverIcon={faCopySolid}
                            onClick={() => copyToClipboard(eventId)}
                        />
                        :
                    </strong>{' '}
                    <pre>{eventId}</pre>
                </div>
            )}
            <div style={{ display: 'flex', gap: '1rem' }}>
                <LinkButton href="/">Go Home</LinkButton>
                <Button type="button" onClick={props.resetError}>
                    Try to Recover
                </Button>
            </div>
            {getIsDebugModeSafely() && <ErrorDetails error={props.error} />}
        </BasePage>
    );
}
